import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import QuoteBuilder from "./pages/QuoteBuilder";
import CustomerDashboard from "./pages/CustomerDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminQuotes from "./pages/AdminQuotes";
import AdminClients from "./pages/AdminClients";
import AdminClientDetail from "./pages/AdminClientDetail";
import CatalogManager from "./pages/CatalogManager";
import QuoteDetail from "./pages/QuoteDetail";
import PublicQuote from "./pages/PublicQuote";
import PublicOnboarding from "./pages/PublicOnboarding";
import AdminOnboarding from "./pages/AdminOnboarding";
import AdminUsers from "./pages/AdminUsers";
import Settings from "./pages/Settings";
import SetPassword from "./pages/SetPassword";
import SalesDashboard from "./pages/SalesDashboard";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center font-mono text-sm text-muted-foreground">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function DashboardRouter() {
  const { user } = useAuth();
  if (user?.role === "admin") return <Navigate to="/admin" replace />;
  if (user?.role === "salesperson") return <Navigate to="/sales" replace />;
  return <CustomerDashboard />;
}

// Hide the "Made with Emergent" badge on all pages.
function BadgeVisibility() {
  const location = useLocation();
  useEffect(() => {
    const badge = document.getElementById("emergent-badge");
    if (badge) badge.style.display = "none";
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AuthProvider>
          <Navbar />
          <BadgeVisibility />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/build" element={<QuoteBuilder />} />
            <Route path="/q/:token" element={<PublicQuote />} />
            <Route path="/onboarding/:token" element={<PublicOnboarding />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales"
              element={
                <ProtectedRoute>
                  <SalesDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/quotes"
              element={
                <ProtectedRoute adminOnly>
                  <AdminQuotes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <ProtectedRoute adminOnly>
                  <AdminClients />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clients/:email"
              element={
                <ProtectedRoute adminOnly>
                  <AdminClientDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/catalog"
              element={
                <ProtectedRoute adminOnly>
                  <CatalogManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute adminOnly>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/onboarding"
              element={
                <ProtectedRoute adminOnly>
                  <AdminOnboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotes/:id"
              element={
                <ProtectedRoute>
                  <QuoteDetail />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
